import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Save, Plus, Trash2, Mail, MessageCircle, ChevronDown, ExternalLink } from 'lucide-react';

const greenColor = '#95DE1A';
const greenGradient = 'linear-gradient(135deg, #95DE1A 0%, #7EC50A 100%)';

const SAMPLE_SCENARIOS = {
  'screenshotExample': {
    name: 'Screenshot Example - Visit Page & Capture',
    steps: [
      { "command": "open", "target": "https://example.com", "value": "" },
      { "command": "waitForElementPresent", "target": "css=h1", "value": "10000" },
      { "command": "pause", "target": "", "value": "2000" }
    ]
  },
  'loginTest': {
    name: 'Login Test (sample site)',
    steps: [
      { "command": "open", "target": "https://example.com/login", "value": "" },
      { "command": "type", "target": "id=email", "value": "{{EMAIL}}" },
      { "command": "type", "target": "id=password", "value": "{{PASSWORD}}" },
      { "command": "click", "target": "css=button[type='submit']", "value": "" },
      { "command": "waitForElementPresent", "target": "css=.dashboard", "value": "10000" },
      { "command": "verifyTextPresent", "target": "Welcome", "value": "" }
    ]
  },
  'formSubmit': {
    name: 'Contact Form Submit',
    steps: [
      { "command": "open", "target": "https://example.com/contact", "value": "" },
      { "command": "type", "target": "name=name", "value": "Test User" },
      { "command": "type", "target": "name=email", "value": "test@example.com" },
      { "command": "type", "target": "name=message", "value": "This is a test message." },
      { "command": "click", "target": "css=button[type='submit']", "value": "" },
      { "command": "waitForElementPresent", "target": "css=.success-message", "value": "10000" }
    ]
  },
  'katalonExample': {
    name: 'Katalon Recorder Example (Admin Panel)',
    steps: [
      {"command":"open","target":"https://adembayazit.com/MANAGE-ENTRIES/index.html","value":""},
      {"command":"type","target":"id=adminPassword","value":"*birdahabuhatayiyapma*"},
      {"command":"click","target":"xpath=//i","value":""},
      {"command":"waitForElementPresent","target":"css=.dashboard","value":"10000"},
      {"command":"open","target":"https://adembayazit.com/MANAGE-ENTRIES/admin.html","value":""},
      {"command":"type","target":"id=enContent","value":"synthetic record test entry"},
      {"command":"click","target":"//button[@onclick='submitEntry()']","value":""},
      {"command":"waitForElementPresent","target":"css=.success-message","value":"10000"}
    ]
  }
};

function EditSyntheticModal({ test, onClose, onSubmit, darkMode = false }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    intervalSeconds: 300,
    steps: '',
    notifyEmail: true,
    notifyTelegram: true,
    timeoutSeconds: 60,
    isActive: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [secretInputs, setSecretInputs] = useState([]);

  useEffect(() => {
    if (test) {
      const initialSecrets = test.secrets?.map(s => ({ key: s.key, value: '' })) || [];
      setSecretInputs(initialSecrets);
      setFormData({
        name: test.name || '',
        description: test.description || '',
        intervalSeconds: test.intervalSeconds || 300,
        steps: JSON.stringify(test.steps || [], null, 2),
        notifyEmail: test.notifyEmail !== undefined ? test.notifyEmail : true,
        notifyTelegram: test.notifyTelegram !== undefined ? test.notifyTelegram : true,
        timeoutSeconds: test.timeoutSeconds || 60,
        isActive: test.isActive !== undefined ? test.isActive : true,
      });
    }
  }, [test]);

  const handleAddSecret = () => {
    setSecretInputs([...secretInputs, { key: '', value: '' }]);
  };

  const handleSecretChange = (index, field, value) => {
    const newInputs = [...secretInputs];
    newInputs[index][field] = value;
    setSecretInputs(newInputs);
  };

  const handleRemoveSecret = (index) => {
    const newInputs = secretInputs.filter((_, i) => i !== index);
    setSecretInputs(newInputs);
  };

  const toggleNotification = (field) => {
    setFormData({ ...formData, [field]: !formData[field] });
  };

  const loadSampleScenario = (key) => {
    const scenario = SAMPLE_SCENARIOS[key];
    if (scenario) {
      setFormData({
        ...formData,
        name: scenario.name,
        steps: JSON.stringify(scenario.steps, null, 2)
      });
    }
  };

  const clearSteps = () => {
    setFormData({ ...formData, steps: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Test name is required');
      return;
    }

    let stepsJson;
    try {
      stepsJson = JSON.parse(formData.steps);
      if (!Array.isArray(stepsJson)) {
        setError('Steps must be a JSON array');
        return;
      }
    } catch (err) {
      setError('Invalid JSON in steps field');
      return;
    }

    const secrets = secretInputs
      .filter(s => s.key.trim() && s.value.trim())
      .map(s => ({ key: s.key.trim(), value: s.value.trim() }));

    setLoading(true);
    setError('');

    try {
      const result = await onSubmit({
        ...formData,
        steps: stepsJson,
        secrets,
      });

      if (!result) {
        setError('No response from server');
        return;
      }

      if (result.success === true) {
        onClose();
      } else {
        setError(result.error || 'Failed to update test');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError('An unexpected error occurred: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-white'}`}>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: greenGradient }}>
              <Save className="h-5 w-5 text-white" />
            </div>
            <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Edit Synthetic Test</h2>
          </div>
          <button onClick={onClose} className={`p-2 rounded-xl flex-shrink-0 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
            <X className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="flex items-center space-x-3 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700 dark:text-red-300 break-words">{error}</span>
            </div>
          )}

          {/* Enable Monitoring Toggle */}
          <div className={`rounded-xl p-4 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <label className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Enable Monitoring</label>
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Turn on/off this test</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                  style={{ backgroundColor: formData.isActive ? greenColor : '#D1D5DB' }}
                ></div>
              </label>
            </div>
          </div>

          {/* Katalon Promo Banner - Mobil uyumlu */}
          <div className={`p-4 rounded-xl border flex flex-col sm:flex-row items-start gap-3 ${darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-blue-800' : 'bg-blue-100'}`}>
              <ExternalLink className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className={`font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Using Katalon Recorder?</h3>
              <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'} break-words`}>
                Export your test from Katalon Recorder as <strong>JSON (Selenium IDE)</strong> and paste it into the Steps field. 
                All common Selenium commands are supported.
              </p>
              <a
                href="https://chrome.google.com/webstore/detail/katalon-recorder/ljdobmomdgdljnioijhopjelfpkbipdn"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline break-words"
              >
                Get Katalon Recorder for Chrome
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
              </a>
            </div>
          </div>

          {/* Sample Scenarios Dropdown */}
          <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Load Sample Scenario</label>
            <div className="relative">
              <select
                onChange={(e) => loadSampleScenario(e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                defaultValue=""
              >
                <option value="" disabled>Choose a sample...</option>
                {Object.entries(SAMPLE_SCENARIOS).map(([key, val]) => (
                  <option key={key} value={key}>{val.name}</option>
                ))}
              </select>
              <ChevronDown className={`absolute right-3 top-3 h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'} pointer-events-none`} />
            </div>
            <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'} break-words`}>
              Selecting a sample will overwrite current name and steps.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Test Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
              />
            </div>
            <div>
              <label className={`block text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="2"
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Check Interval (seconds)</label>
              <select
                value={formData.intervalSeconds}
                onChange={(e) => setFormData({ ...formData, intervalSeconds: parseInt(e.target.value, 10) })}
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
              >
                <option value="60">1 minute</option>
                <option value="300">5 minutes</option>
                <option value="600">10 minutes</option>
                <option value="900">15 minutes</option>
                <option value="1800">30 minutes</option>
                <option value="3600">1 hour</option>
                <option value="21600">6 hours</option>
                <option value="43200">12 hours</option>
                <option value="86400">24 hours</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Timeout (seconds)</label>
              <input
                type="number"
                min="10"
                max="300"
                value={formData.timeoutSeconds}
                onChange={(e) => setFormData({ ...formData, timeoutSeconds: parseInt(e.target.value, 10) })}
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
              />
            </div>
          </div>

          {/* STEPS TEXTAREA - Mobilde taşma düzeltildi */}
          <div>
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Test Steps (JSON)</label>
              <button
                type="button"
                onClick={clearSteps}
                className="text-xs text-red-600 dark:text-red-400 hover:underline"
              >
                Clear
              </button>
            </div>
            <textarea
              value={formData.steps}
              onChange={(e) => setFormData({ ...formData, steps: e.target.value })}
              rows="10"
              className={`w-full px-4 py-3 border rounded-xl font-mono text-sm focus:outline-none focus:ring-2 focus:ring-green-500 overflow-x-auto whitespace-pre-wrap break-words ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
              style={{ wordBreak: 'break-all', overflowWrap: 'break-word' }}
            />
            <p className={`mt-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'} break-words`}>
              Paste your Katalon Recorder JSON (Selenium IDE format) here.
            </p>
          </div>

          {/* Secrets Section - Mobil uyumlu */}
          <div className={`border-t pt-6 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Secrets (leave blank to keep current)</h3>
              <button
                type="button"
                onClick={handleAddSecret}
                className="px-3 py-1.5 text-sm text-white rounded-lg hover:opacity-90 flex items-center space-x-1"
                style={{ background: greenGradient }}
              >
                <Plus className="h-4 w-4" />
                <span>Add Secret</span>
              </button>
            </div>

            {secretInputs.map((secret, index) => (
              <div key={index} className={`flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-3 p-3 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <input
                  type="text"
                  placeholder="Key"
                  value={secret.key}
                  onChange={(e) => handleSecretChange(index, 'key', e.target.value)}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'}`}
                />
                <input
                  type="password"
                  placeholder="New value (leave empty to keep current)"
                  value={secret.value}
                  onChange={(e) => handleSecretChange(index, 'value', e.target.value)}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'}`}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveSecret(index)}
                  className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg self-end sm:self-auto"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            {secretInputs.length === 0 && (
              <p className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>No secrets configured.</p>
            )}
          </div>

          {/* Notification Preferences */}
          <div className={`border-t pt-6 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Notification Preferences</h3>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => toggleNotification('notifyEmail')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl border transition ${
                  formData.notifyEmail 
                    ? (darkMode ? 'bg-green-900/40 text-green-400 border border-green-500/50' : 'bg-green-100 border-green-200 text-green-700')
                    : (darkMode ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500')
                }`}
              >
                <Mail className="h-4 w-4" />
                <span>Email</span>
              </button>
              <button
                type="button"
                onClick={() => toggleNotification('notifyTelegram')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl border transition ${
                  formData.notifyTelegram 
                    ? (darkMode ? 'bg-sky-900/40 text-sky-400 border border-sky-500/50' : 'bg-blue-100 border-blue-200 text-blue-700')
                    : (darkMode ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500')
                }`}
              >
                <MessageCircle className="h-4 w-4" />
                <span>Telegram</span>
              </button>
            </div>
          </div>

          {/* Buttons - Mobil uyumlu */}
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-6">
            <button 
              type="button" 
              onClick={onClose} 
              className={`flex-1 px-6 py-3 text-sm font-semibold rounded-2xl transition-all ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 text-sm font-semibold text-white rounded-2xl transition-all disabled:opacity-50"
              style={{ background: greenGradient }}
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Saving...</span>
                </div>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditSyntheticModal;